import { sendMessageToModel } from '@/services/ModelService';
import { useSupabaseStore } from '@/store/SupabaseStore';
import type { AIModel, Message } from '@/types';
import { getSelectedToolsForAgent } from '@/lib/agentTools';

export type MapleClawEventType = 'user_message' | 'heartbeat';
export type MapleClawRuntime = 'electron' | 'browser' | 'unavailable';
export type MapleClawProcessingRuntime = 'local' | 'pi-agent';

export type MapleClawTranscriptEntry = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  eventType: MapleClawEventType;
};

export type MapleClawSessionMeta = {
  key: string;
  id: string;
  createdAt: string;
  lastActivityAt: string;
  lastEventType: MapleClawEventType;
  lastAssistantReply?: string;
  transcriptPath: string;
  queueSize: number;
  processing: boolean;
};

export type MapleClawConfig = {
  workspacePath: string;
  heartbeatMs: number;
  autoHeartbeat: boolean;
  defaultSessionKey: string;
  defaultModelId?: string;
  processingRuntime: MapleClawProcessingRuntime;
  piAgentModelId?: string;
  piAgentInstructions?: string;
};

export type MapleClawStatus = {
  available: boolean;
  running: boolean;
  runtime: MapleClawRuntime;
  workspacePath: string;
  heartbeatMs: number;
  autoHeartbeat: boolean;
  sessionCount: number;
  activeSessions: number;
  processingRuntime: MapleClawProcessingRuntime;
};

export type MapleClawEnqueueInput = {
  sessionKey?: string;
  type: MapleClawEventType;
  payload?: string;
  awaitResponse?: boolean;
  timeoutMs?: number;
  modelId?: string;
  processingRuntime?: MapleClawProcessingRuntime;
  agentInstruction?: string;
};

export type MapleClawEnqueueResult = {
  ok: boolean;
  eventId: string;
  content?: string;
  error?: string;
};

type QueuedEvent = {
  id: string;
  sessionKey: string;
  type: MapleClawEventType;
  payload: string;
  modelId?: string;
  processingRuntime: MapleClawProcessingRuntime;
  agentInstruction?: string;
  createdAt: string;
  awaitResponse: boolean;
  resolve?: (value: MapleClawEnqueueResult) => void;
};

type PersistedSessions = Record<string, Omit<MapleClawSessionMeta, 'queueSize' | 'processing'>>;
type ElectronBridge = NonNullable<Window['electronAPI']>;

const MAPLECLAW_DEFAULTS: MapleClawConfig = {
  workspacePath: 'MapleClaw',
  heartbeatMs: 30 * 60 * 1000,
  autoHeartbeat: true,
  defaultSessionKey: 'main',
  processingRuntime: 'local',
  piAgentInstructions: '',
};

const SETTINGS_APP_ID = 'mapleclaw';
const SESSIONS_FILE = 'sessions.json';
const TRANSCRIPTS_DIR = 'sessions';
const UPDATE_EVENT = 'mapleclaw:update';
const MAX_CONTEXT_ENTRIES = 24;
const DEFAULT_TRANSCRIPT_TAIL = 50;
const BROWSER_KEY_PREFIX = 'mapleclaw';
const BROWSER_SETTINGS_KEY = `${BROWSER_KEY_PREFIX}:settings`;

function isElectronAvailable() {
  return typeof window !== 'undefined' && Boolean(window.electronAPI?.isElectron);
}

function isBrowserStorageAvailable() {
  if (typeof window === 'undefined') return false;
  try {
    const key = `${BROWSER_KEY_PREFIX}:availability`;
    window.localStorage.setItem(key, '1');
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function getMapleClawRuntime(): MapleClawRuntime {
  if (isElectronAvailable()) return 'electron';
  if (isBrowserStorageAvailable()) return 'browser';
  return 'unavailable';
}

function getElectronAPI(): ElectronBridge | null {
  if (!isElectronAvailable()) return null;
  return window.electronAPI as ElectronBridge;
}

function nowIso() {
  return new Date().toISOString();
}

function createId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function normalizeWorkspacePath(value?: string) {
  const raw = String(value || MAPLECLAW_DEFAULTS.workspacePath).trim().replace(/[\\/]+/g, '\\');
  return raw.replace(/^\\+|\\+$/g, '') || MAPLECLAW_DEFAULTS.workspacePath;
}

function normalizeSessionKey(value?: string) {
  return String(value || MAPLECLAW_DEFAULTS.defaultSessionKey).trim() || MAPLECLAW_DEFAULTS.defaultSessionKey;
}

function normalizeProcessingRuntime(value?: string): MapleClawProcessingRuntime {
  return value === 'pi-agent' ? 'pi-agent' : 'local';
}

function truncate(text: string, max = 160) {
  const normalized = String(text || '').trim().replace(/\s+/g, ' ');
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 1)}…`;
}

class MapleClawGateway {
  private readyPromise: Promise<void> | null = null;
  private config: MapleClawConfig = { ...MAPLECLAW_DEFAULTS };
  private sessions = new Map<string, MapleClawSessionMeta>();
  private queues = new Map<string, QueuedEvent[]>();
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  private get runtime() {
    return getMapleClawRuntime();
  }

  private get workspaceRoot() {
    return normalizeWorkspacePath(this.config.workspacePath);
  }

  private get sessionsFilePath() {
    return `${this.workspaceRoot}\\${SESSIONS_FILE}`;
  }

  private get transcriptsFolderPath() {
    return `${this.workspaceRoot}\\${TRANSCRIPTS_DIR}`;
  }

  private browserStorageKey(path: string) {
    return `${BROWSER_KEY_PREFIX}:file:${path}`;
  }

  private buildTranscriptPath(sessionKey: string) {
    if (this.runtime === 'browser') {
      const root = this.workspaceRoot.replace(/\\/g, '/');
      return `browser://${root}/sessions/${normalizeSessionKey(sessionKey)}.jsonl`;
    }
    return `${this.transcriptsFolderPath}\\${normalizeSessionKey(sessionKey)}.jsonl`;
  }

  private emitUpdate(reason: string) {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: { reason, timestamp: nowIso() } }));
  }

  private async readPersistedFile(path: string) {
    if (this.runtime === 'electron') {
      const api = getElectronAPI();
      if (!api) return null;
      return await api.readFile(path).catch(() => null);
    }

    if (this.runtime === 'browser') {
      try {
        return window.localStorage.getItem(this.browserStorageKey(path));
      } catch {
        return null;
      }
    }

    return null;
  }

  private async writePersistedFile(path: string, content: string) {
    if (this.runtime === 'electron') {
      const api = getElectronAPI();
      if (!api) return;
      await api.writeFile(path, content);
      return;
    }

    if (this.runtime === 'browser') {
      try {
        window.localStorage.setItem(this.browserStorageKey(path), content);
      } catch {
        // Ignore storage quota/write failures and let runtime continue in-memory.
      }
    }
  }

  private async ensureDirectory(path: string) {
    if (this.runtime !== 'electron') return;
    const api = getElectronAPI();
    if (!api) return;
    await api.createDirectory(path).catch(() => undefined);
  }

  private async loadStoredConfig() {
    if (this.runtime === 'electron') {
      const api = getElectronAPI();
      if (!api) return null;
      return await api.loadSettings(SETTINGS_APP_ID).catch(() => null);
    }

    if (this.runtime === 'browser') {
      try {
        const raw = window.localStorage.getItem(BROWSER_SETTINGS_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    }

    return null;
  }

  private async persistConfig() {
    if (this.runtime === 'electron') {
      const api = getElectronAPI();
      if (!api) return;
      await api.saveSettings(SETTINGS_APP_ID, this.config);
      return;
    }

    if (this.runtime === 'browser') {
      try {
        window.localStorage.setItem(BROWSER_SETTINGS_KEY, JSON.stringify(this.config));
      } catch {
        // Ignore storage quota/write failures and let runtime continue in-memory.
      }
    }
  }

  async ensureReady() {
    if (this.runtime === 'unavailable') return;

    if (!this.readyPromise) {
      this.readyPromise = this.initialize();
    }

    await this.readyPromise;
  }

  private async initialize() {
    const savedConfig = await this.loadStoredConfig();
    this.config = {
      ...MAPLECLAW_DEFAULTS,
      ...(savedConfig || {}),
      workspacePath: normalizeWorkspacePath(savedConfig?.workspacePath),
      processingRuntime: normalizeProcessingRuntime(savedConfig?.processingRuntime),
      piAgentInstructions: String(savedConfig?.piAgentInstructions || ''),
    };

    await this.ensureDirectory(this.workspaceRoot);
    await this.ensureDirectory(this.transcriptsFolderPath);

    const rawSessions = await this.readPersistedFile(this.sessionsFilePath);
    if (rawSessions) {
      try {
        const parsed = JSON.parse(rawSessions) as PersistedSessions;
        Object.entries(parsed || {}).forEach(([key, session]) => {
          this.sessions.set(key, {
            ...session,
            transcriptPath: session.transcriptPath || this.buildTranscriptPath(key),
            queueSize: 0,
            processing: false,
          });
        });
      } catch {
        this.sessions.clear();
      }
    }

    this.syncHeartbeatTimer();
    this.emitUpdate('ready');
  }

  private async persistSessions() {
    const persisted: PersistedSessions = {};
    this.sessions.forEach((session, key) => {
      persisted[key] = {
        key: session.key,
        id: session.id,
        createdAt: session.createdAt,
        lastActivityAt: session.lastActivityAt,
        lastEventType: session.lastEventType,
        lastAssistantReply: session.lastAssistantReply,
        transcriptPath: session.transcriptPath,
      };
    });

    await this.writePersistedFile(this.sessionsFilePath, JSON.stringify(persisted, null, 2));
  }

  private syncHeartbeatTimer() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }

    if (!this.config.autoHeartbeat || this.runtime === 'unavailable') return;

    this.heartbeatTimer = setInterval(() => {
      void this.enqueue({
        sessionKey: this.config.defaultSessionKey,
        type: 'heartbeat',
        payload: 'Scheduled MapleClaw heartbeat',
        awaitResponse: false,
      });
    }, clamp(this.config.heartbeatMs, 15_000, 24 * 60 * 60 * 1000));
  }

  private async getOrCreateSession(sessionKey: string) {
    const normalizedKey = normalizeSessionKey(sessionKey);
    const existing = this.sessions.get(normalizedKey);
    if (existing) return existing;

    const createdAt = nowIso();
    const session: MapleClawSessionMeta = {
      key: normalizedKey,
      id: createId(),
      createdAt,
      lastActivityAt: createdAt,
      lastEventType: 'heartbeat',
      transcriptPath: this.buildTranscriptPath(normalizedKey),
      queueSize: 0,
      processing: false,
    };

    this.sessions.set(normalizedKey, session);
    this.queues.set(normalizedKey, []);
    await this.persistSessions();
    this.emitUpdate('session-created');
    return session;
  }

  private async appendTranscriptEntry(session: MapleClawSessionMeta, entry: MapleClawTranscriptEntry) {
    const current = (await this.readPersistedFile(session.transcriptPath)) || '';
    const nextContent = current ? `${current.trimEnd()}\n${JSON.stringify(entry)}` : JSON.stringify(entry);
    await this.writePersistedFile(session.transcriptPath, `${nextContent}\n`);
  }

  private async readTranscriptEntries(sessionKey: string, tail = DEFAULT_TRANSCRIPT_TAIL) {
    await this.ensureReady();
    const session = await this.getOrCreateSession(sessionKey);
    const raw = await this.readPersistedFile(session.transcriptPath);
    if (!raw) return [] as MapleClawTranscriptEntry[];

    const lines = raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    const parsed = lines
      .map((line) => {
        try {
          return JSON.parse(line) as MapleClawTranscriptEntry;
        } catch {
          return null;
        }
      })
      .filter((entry): entry is MapleClawTranscriptEntry => Boolean(entry));

    if (tail <= 0) return parsed;
    return parsed.slice(-tail);
  }

  private async selectModel(explicitModelId?: string) {
    const store = useSupabaseStore.getState();
    if (!store.models?.length) {
      await store.fetchModels();
    }

    const models = useSupabaseStore.getState().models || [];
    const preferredId = explicitModelId || this.config.defaultModelId;

    let model = preferredId ? models.find((candidate) => candidate.id === preferredId) : undefined;
    if (!model) {
      model = models.find((candidate) => candidate.provider === 'openai' && candidate.apiName.includes('gpt')) || models[0];
    }

    return { model, models };
  }

  private async selectPiAgentModel(explicitModelId?: string) {
    const store = useSupabaseStore.getState();
    if (!store.models?.length) {
      await store.fetchModels();
    }

    const models = useSupabaseStore.getState().models || [];
    const preferredId = explicitModelId || this.config.piAgentModelId || this.config.defaultModelId;

    let model = preferredId ? models.find((candidate) => candidate.id === preferredId) : undefined;
    if (!model) {
      model = models.find((candidate) => candidate.provider === 'maplept')
        || models.find((candidate) => candidate.id === 'maplept-free')
        || models.find((candidate) => candidate.provider === 'openai')
        || models[0];
    }

    return { model, models };
  }

  private buildConversationMessages(entries: MapleClawTranscriptEntry[], prompt: string, systemPrompts: string[]): Message[] {
    const historyMessages: Message[] = entries.slice(-MAX_CONTEXT_ENTRIES).map((entry): Message => ({
      id: entry.id,
      chatId: 'mapleclaw',
      content: entry.content,
      role: entry.role,
      sender: entry.role === 'user' ? 'user' : 'ai',
      timestamp: entry.timestamp,
    }));

    const systemMessages: Message[] = systemPrompts
      .filter(Boolean)
      .map((content): Message => ({
        id: createId(),
        chatId: 'mapleclaw',
        role: 'system',
        sender: 'ai',
        timestamp: nowIso(),
        content,
      }));

    const userMessage: Message = {
      id: createId(),
      chatId: 'mapleclaw',
      role: 'user',
      sender: 'user',
      timestamp: nowIso(),
      content: prompt,
    };

    return [...systemMessages, ...historyMessages, userMessage];
  }

  private async processLocalUserMessage(event: QueuedEvent, session: MapleClawSessionMeta) {
    const previousEntries = await this.readTranscriptEntries(session.key, MAX_CONTEXT_ENTRIES);
    const userEntry: MapleClawTranscriptEntry = {
      id: createId(),
      role: 'user',
      content: event.payload,
      timestamp: nowIso(),
      eventType: 'user_message',
    };
    await this.appendTranscriptEntry(session, userEntry);

    const { model, models } = await this.selectModel(event.modelId);
    let responseContent = '';

    if (model && models.length) {
      const messages = this.buildConversationMessages(previousEntries, event.payload, [
        'You are MapleClaw, MapleOS\'s local control-plane assistant. Keep responses concise, operational, and grounded in the current session history. Treat heartbeats as maintenance checks and user messages as actionable operator requests.',
      ]);
      const response = await sendMessageToModel(messages, model as AIModel, models);
      responseContent = String(response.content || '').trim();
    }

    if (!responseContent) {
      responseContent = `MapleClaw queued your request for session "${session.key}": ${truncate(event.payload, 120)}`;
    }

    const assistantEntry: MapleClawTranscriptEntry = {
      id: createId(),
      role: 'assistant',
      content: responseContent,
      timestamp: nowIso(),
      eventType: 'user_message',
    };
    await this.appendTranscriptEntry(session, assistantEntry);

    session.lastAssistantReply = responseContent;
    session.lastActivityAt = assistantEntry.timestamp;
    session.lastEventType = 'user_message';
    await this.persistSessions();

    return responseContent;
  }

  private async processPiAgentMessage(event: QueuedEvent, session: MapleClawSessionMeta) {
    const previousEntries = await this.readTranscriptEntries(session.key, MAX_CONTEXT_ENTRIES);
    const userEntry: MapleClawTranscriptEntry = {
      id: createId(),
      role: 'user',
      content: event.payload,
      timestamp: nowIso(),
      eventType: 'user_message',
    };
    await this.appendTranscriptEntry(session, userEntry);

    const enabledTools = getSelectedToolsForAgent();
    const osToolNames = enabledTools.selected.osApps.map((tool) => tool.name).join(', ');
    const mcpToolNames = enabledTools.selected.mcpServices.map((tool) => tool.name).join(', ');
    const toolInventory = [
      osToolNames ? `Enabled OS tools: ${osToolNames}.` : 'Enabled OS tools: none.',
      mcpToolNames ? `Enabled MCP services: ${mcpToolNames}.` : 'Enabled MCP services: none.',
    ].join(' ');

    const { model, models } = await this.selectPiAgentModel(event.modelId);
    let responseContent = '';

    if (model && models.length) {
      const messages = this.buildConversationMessages(previousEntries, event.payload, [
        'You are pi-agent operating through MapleClaw. You are a runtime-oriented operator agent: summarize state, propose next actions, identify tool usage opportunities, and keep replies concise and actionable. Do not claim a tool has been executed unless the transcript explicitly shows it.',
        toolInventory,
        event.agentInstruction || this.config.piAgentInstructions || '',
      ]);
      const response = await sendMessageToModel(messages, model as AIModel, models);
      responseContent = String(response.content || '').trim();
    }

    if (!responseContent) {
      responseContent = `pi-agent reviewed session "${session.key}" and is waiting for the next action.`;
    }

    const assistantEntry: MapleClawTranscriptEntry = {
      id: createId(),
      role: 'assistant',
      content: responseContent,
      timestamp: nowIso(),
      eventType: 'user_message',
    };
    await this.appendTranscriptEntry(session, assistantEntry);

    session.lastAssistantReply = responseContent;
    session.lastActivityAt = assistantEntry.timestamp;
    session.lastEventType = 'user_message';
    await this.persistSessions();

    return responseContent;
  }

  private async processHeartbeat(event: QueuedEvent, session: MapleClawSessionMeta) {
    const content = event.payload?.trim() || 'HEARTBEAT_OK';
    const entry: MapleClawTranscriptEntry = {
      id: createId(),
      role: 'system',
      content,
      timestamp: nowIso(),
      eventType: 'heartbeat',
    };
    await this.appendTranscriptEntry(session, entry);
    session.lastActivityAt = entry.timestamp;
    session.lastEventType = 'heartbeat';
    await this.persistSessions();
    return content;
  }

  private async processQueue(sessionKey: string) {
    const session = await this.getOrCreateSession(sessionKey);
    if (session.processing) return;

    session.processing = true;
    this.emitUpdate('queue-start');

    const queue = this.queues.get(sessionKey) || [];
    this.queues.set(sessionKey, queue);

    while (queue.length) {
      const event = queue.shift()!;
      session.queueSize = queue.length;
      this.emitUpdate('queue-progress');

      try {
        const content = event.type === 'heartbeat'
          ? await this.processHeartbeat(event, session)
          : event.processingRuntime === 'pi-agent'
            ? await this.processPiAgentMessage(event, session)
            : await this.processLocalUserMessage(event, session);

        event.resolve?.({ ok: true, eventId: event.id, content });
      } catch (error: any) {
        event.resolve?.({
          ok: false,
          eventId: event.id,
          error: error?.message || 'MapleClaw failed to process the event.',
        });
      }
    }

    session.processing = false;
    session.queueSize = 0;
    this.emitUpdate('queue-idle');
  }

  async enqueue(input: MapleClawEnqueueInput): Promise<MapleClawEnqueueResult> {
    await this.ensureReady();
    if (this.runtime === 'unavailable' || typeof window === 'undefined') {
      return {
        ok: false,
        eventId: createId(),
        error: 'MapleClaw is unavailable in the current runtime.',
      };
    }

    const sessionKey = normalizeSessionKey(input.sessionKey);
    const session = await this.getOrCreateSession(sessionKey);
    const queue = this.queues.get(sessionKey) || [];
    this.queues.set(sessionKey, queue);

    const event: QueuedEvent = {
      id: createId(),
      sessionKey,
      type: input.type,
      payload: String(input.payload || (input.type === 'heartbeat' ? 'HEARTBEAT_OK' : '')).trim(),
      modelId: input.modelId,
      processingRuntime: normalizeProcessingRuntime(input.processingRuntime || this.config.processingRuntime),
      agentInstruction: String(input.agentInstruction || ''),
      createdAt: nowIso(),
      awaitResponse: Boolean(input.awaitResponse),
    };

    if (!event.payload && event.type === 'user_message') {
      return {
        ok: false,
        eventId: event.id,
        error: 'MapleClaw requires a message payload for send-message events.',
      };
    }

    queue.push(event);
    session.queueSize = queue.length;
    this.emitUpdate('queue-enqueued');

    const processingPromise = this.processQueue(sessionKey);
    if (!event.awaitResponse) {
      void processingPromise;
      return { ok: true, eventId: event.id, content: 'Queued' };
    }

    return await new Promise<MapleClawEnqueueResult>((resolve) => {
      const timeoutMs = clamp(Number(input.timeoutMs || 30_000), 1_000, 120_000);
      const timer = window.setTimeout(() => {
        resolve({ ok: false, eventId: event.id, error: 'MapleClaw timed out waiting for a response.' });
      }, timeoutMs);

      event.resolve = (result) => {
        window.clearTimeout(timer);
        resolve(result);
      };

      void processingPromise;
    });
  }

  async getStatus(): Promise<MapleClawStatus> {
    await this.ensureReady();
    const sessions = Array.from(this.sessions.values());
    return {
      available: this.runtime !== 'unavailable',
      running: this.runtime !== 'unavailable',
      runtime: this.runtime,
      workspacePath: this.workspaceRoot,
      heartbeatMs: this.config.heartbeatMs,
      autoHeartbeat: this.config.autoHeartbeat,
      sessionCount: sessions.length,
      activeSessions: sessions.filter((session) => session.processing || session.queueSize > 0).length,
      processingRuntime: this.config.processingRuntime,
    };
  }

  async getConfig() {
    await this.ensureReady();
    return { ...this.config };
  }

  async listSessions() {
    await this.ensureReady();
    return Array.from(this.sessions.values())
      .map((session) => ({
        ...session,
        queueSize: this.queues.get(session.key)?.length || 0,
        processing: Boolean(session.processing),
      }))
      .sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt));
  }

  async getTranscript(sessionKey: string, tail = DEFAULT_TRANSCRIPT_TAIL) {
    return this.readTranscriptEntries(sessionKey, clamp(Number(tail || DEFAULT_TRANSCRIPT_TAIL), 1, 200));
  }

  async setConfig(nextConfig: Partial<MapleClawConfig>) {
    await this.ensureReady();
    this.config = {
      ...this.config,
      ...nextConfig,
      workspacePath: normalizeWorkspacePath(nextConfig.workspacePath || this.config.workspacePath),
      heartbeatMs: clamp(Number(nextConfig.heartbeatMs || this.config.heartbeatMs), 15_000, 24 * 60 * 60 * 1000),
      autoHeartbeat: nextConfig.autoHeartbeat ?? this.config.autoHeartbeat,
      defaultSessionKey: normalizeSessionKey(nextConfig.defaultSessionKey || this.config.defaultSessionKey),
      processingRuntime: normalizeProcessingRuntime(nextConfig.processingRuntime || this.config.processingRuntime),
      piAgentInstructions: String(nextConfig.piAgentInstructions ?? this.config.piAgentInstructions ?? ''),
    };

    await this.ensureDirectory(this.workspaceRoot);
    await this.ensureDirectory(this.transcriptsFolderPath);
    await this.persistConfig();
    await this.persistSessions();
    this.syncHeartbeatTimer();
    this.emitUpdate('config-updated');
    return this.config;
  }

  subscribe(callback: (reason: string) => void) {
    if (typeof window === 'undefined') return () => undefined;

    const handler = (event: Event) => {
      callback((event as CustomEvent<{ reason?: string }>).detail?.reason || 'updated');
    };

    window.addEventListener(UPDATE_EVENT, handler as EventListener);
    return () => window.removeEventListener(UPDATE_EVENT, handler as EventListener);
  }
}

export const mapleClawGateway = new MapleClawGateway();

export function isMapleClawAvailable() {
  return getMapleClawRuntime() !== 'unavailable';
}
